import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { REVIEWS } from '@/constants/testIds';

const testimonials = [
  {
    name: 'Aanya S.',
    role: 'Product designer, 29',
    rating: 5,
    text: 'The Bedtime Tea has become my quiet armour against a loud week. I sleep deeper and wake softer.',
    img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=300&q=80',
    tea: 'Bedtime Tea',
  },
  {
    name: 'Meera K.',
    role: 'Founder, wellness studio',
    rating: 5,
    text: "Women's Care is the first cycle-support tea I've actually stayed with. My skin is calmer, so am I.",
    img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=300&q=80',
    tea: "Women's Care Tea",
  },
  {
    name: 'Zara P.',
    role: 'Journalist, 34',
    rating: 5,
    text: 'Detox in the morning replaced my second coffee. Bright, warming, and I feel lighter by mid-day.',
    img: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=300&q=80',
    tea: 'Detox Tea',
  },
  {
    name: 'Riya B.',
    role: 'Yoga teacher',
    rating: 5,
    text: 'It feels like the tea my grandmother would have made — but designed for the life I actually live.',
    img: 'https://images.unsplash.com/photo-1541823709867-1b206113eafd?auto=format&fit=crop&w=300&q=80',
    tea: 'Bedtime Tea',
  },
  {
    name: 'Nisha R.',
    role: 'Doctor, 31',
    rating: 5,
    text: 'Clean ingredients I can trust and a ritual I look forward to. Rare combo.',
    img: 'https://images.unsplash.com/photo-1548142813-c348350df52b?auto=format&fit=crop&w=300&q=80',
    tea: 'Detox Tea',
  },
  {
    name: 'Ishita M.',
    role: 'Consultant, 27',
    rating: 5,
    text: 'The packaging alone makes my kitchen feel like a small tea house. Then you taste it.',
    img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80',
    tea: "Women's Care Tea",
  },
];

function Counter({ to, suffix = '', duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * to));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, to, duration]);

  return (
    <span ref={ref}>
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

const stats = [
  { slug: 'satisfaction', label: 'Customer satisfaction', value: 98, suffix: '%' },
  { slug: 'cups', label: 'Cups steeped worldwide', value: 220000, suffix: '+' },
  { slug: 'repeat', label: 'Repeat purchase rate', value: 87, suffix: '%' },
];

export default function Reviews() {
  const track = [...testimonials, ...testimonials];

  return (
    <section
      id="reviews"
      data-testid={REVIEWS.root}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <p className="text-xs uppercase tracking-[0.24em] text-[#2D8B5B] mb-4">Reviews</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1D7448] font-light leading-[1.05]">
            Loved by <span className="italic">our community.</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#4A6756]">
            Small daily rituals, big collective difference. Here&apos;s what the
            Sumaya community is saying.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div
              key={s.slug}
              data-testid={REVIEWS.stat(s.slug)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="rounded-3xl border border-[#E5DCC3] bg-[#FDFBF7] p-8 text-center"
            >
              <div className="font-serif text-5xl md:text-6xl text-[#1D7448] leading-none">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#4A6756]">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Marquee testimonials */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-[#FAF4E3] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-[#FAF4E3] to-transparent z-10" />
        <div className="overflow-hidden">
          <div className="marquee flex gap-6 w-max py-2">
            {track.map((t, i) => (
              <div
                key={i}
                className="w-[320px] md:w-[380px] shrink-0 rounded-3xl bg-[#FDFBF7] border border-[#E5DCC3] p-6 md:p-7 shadow-[0_10px_30px_-15px_rgba(13,58,36,0.15)]"
              >
                <Quote className="w-6 h-6 text-[#2D8B5B]" />
                <div className="flex items-center gap-1 mt-2">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 fill-[#C97B84] text-[#C97B84]" />
                  ))}
                </div>
                <p className="mt-4 text-[15px] leading-relaxed text-[#0D3A24]">
                  {t.text}
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="w-11 h-11 rounded-full object-cover ring-2 ring-[#F3EAD3]"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#1D7448]">{t.name}</p>
                    <p className="text-xs text-[#4A6756]">{t.role} · {t.tea}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
